import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft, Save } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import Link from '@/routing/Link'
import { useParams, useRouter } from '@/routing/navigation'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { populationsApi } from '@/lib/api'
import {
  AudienceFilterEditorPanel,
  getLockedFilterRemovalMessage,
  useAudienceFilterEditor,
} from '@/components/audiences/AudienceFilterEditorPanel'
import { applyFilterCustomLabels } from '@/components/audiences/audience-filter-editor-utils'
import type { AudienceSummary } from '@/lib/api'
import type { ColumnGroupMetadata } from '@/types/routes/tests'

interface ColumnMeta {
  name: string
  displayLabel?: string
  groupDisplayLabel?: string
  canonicalId?: string | null
  category?: string
  uniqueValues?: Array<string>
  valueLabels?: Record<string, string>
  isOperational?: boolean
}

export default function AudienceEditFiltersPage() {
  const { t } = useTranslation('app')
  const { id = '' } = useParams<{ id: string }>()
  const router = useRouter()
  const [audience, setAudience] = useState<AudienceSummary | null>(null)
  const [columns, setColumns] = useState<Array<ColumnMeta>>([])
  const [groups, setGroups] = useState<Array<ColumnGroupMetadata>>([])
  const [selectedColumns, setSelectedColumns] = useState<Array<string> | null>(null)
  const [customLabels, setCustomLabels] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    async function load() {
      if (!id) return
      setIsLoading(true)
      setError(null)
      try {
        const [aud, meta] = await Promise.all([
          populationsApi.get(id),
          populationsApi.getColumnMetadata(id, { includeAllColumns: true }),
        ])
        if (controller.signal.aborted) return
        setAudience(aud)
        setColumns(meta.columns ?? [])
        setGroups(meta.groups ?? [])
        setSelectedColumns(
          Array.isArray(aud.activeFilterColumns) ? aud.activeFilterColumns : null,
        )
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load audience filters:', err)
          setError('Could not load this audience filters.')
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false)
      }
    }
    load()
    return () => controller.abort()
  }, [id])

  // Redirect non-managers back to the audience detail page.
  useEffect(() => {
    if (!isLoading && audience && audience.canEdit === false) {
      router.push(`/audiences/${id}`)
    }
  }, [isLoading, audience, id, router])

  const filterEditor = useAudienceFilterEditor({
    audience,
    columns,
    groups,
    selectedColumns,
    onSelectedColumnsChange: setSelectedColumns,
    customLabels,
    onCustomLabelsChange: setCustomLabels,
  })

  const handleSave = useCallback(async () => {
    if (!id || !audience) return

    const lockedRemovalMessage = getLockedFilterRemovalMessage(filterEditor)
    if (lockedRemovalMessage) {
      toast.error(lockedRemovalMessage)
      return
    }

    setIsSaving(true)
    try {
      if (Object.keys(customLabels).length > 0) {
        const updatedMappings = applyFilterCustomLabels(
          audience.segmentationMappings ?? [],
          customLabels,
          filterEditor.filterGroups,
        )
        await populationsApi.updateSegmentationMappings(id, updatedMappings)
      }
      await populationsApi.updateActiveFilterColumns(id, filterEditor.activeFilterColumns)
      toast.success(t('audienceEdit.filtersUpdated'), { duration: 3000 })
      router.push(`/audiences/${id}`)
    } catch (err) {
      console.error('Failed to save audience filters:', err)
      toast.error(t('audienceEdit.filtersSaveFailed'))
    } finally {
      setIsSaving(false)
    }
  }, [audience, filterEditor, id, router, customLabels, t])

  if (isLoading) {
    return (
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 sm:px-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-3xl" />
        <Skeleton className="h-80 w-full rounded-3xl" />
      </main>
    )
  }

  if (error || !audience) {
    return (
      <main className="mx-auto flex w-full max-w-3xl flex-col items-center gap-4 px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold text-foreground">{t('audienceEdit.filtersUnavailable')}</h1>
        <p className="text-sm text-muted-foreground">{error ?? 'This audience could not be loaded.'}</p>
        <Button asChild variant="outline">
          <Link href={`/audiences/${id}`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('audiences.backToAudience')}
          </Link>
        </Button>
      </main>
    )
  }

  return (
    <main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-8 sm:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-3">
          <Button asChild variant="ghost" className="w-fit px-0">
            <Link href={`/audiences/${id}`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('audiences.backToAudience')}
            </Link>
          </Button>
          <div className="space-y-2">
            <p className="text-sm font-medium uppercase tracking-[0.16em] text-muted-foreground">
              {t('audienceEdit.editFilters')}
            </p>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              {audience.name}
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              Add or remove the filters shown for this audience. Demographic identity
              filters are locked.
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button asChild variant="outline" disabled={isSaving}>
            <Link href={`/audiences/${id}`}>{t('cancel', { ns: 'common' })}</Link>
          </Button>
          <Button
            variant="cta"
            className="gap-2"
            onClick={handleSave}
            disabled={isSaving || !filterEditor.hasChanges}
          >
            <Save className="h-4 w-4" />
            {isSaving ? t('saving', { ns: 'common' }) : t('audienceEdit.saveFilters')}
          </Button>
        </div>
      </div>

      <AudienceFilterEditorPanel editor={filterEditor} />
    </main>
  )
}
